import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const TierList = () => {
  const location = useLocation();
  const { planId } = location.state || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const refreshData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        process.env.REACT_APP_API_TIER_EDIT,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        }
      );

      const tierList = response.data?.data?.tierList || [];

      const groupedBenefits = tierList.map(tier => ({
        id: tier.tierId,
        name: tier.tierName,
        academyNames: tier.academies.map(a => a.academyName)
      }));

      setBenefits(groupedBenefits);
    } catch (error) {
      console.error('Failed to fetch tier list:', error);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const sortedBenefits = [...benefits].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const filtered = sortedBenefits.filter((b) =>
    Object.values(b)
      .flatMap(v => Array.isArray(v) ? v : [v])
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const totalEntries = filtered.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const handleEdit = (event, tierId) => {
    const url = `/tierCreation?Tierid=${tierId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete this item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yep, trash it',
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_DELETE_TIER}/${id}`,
            {
              headers: {
                'Content-Type': 'application/json',
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
              }
            }
          );

          if (response.data.success) {
            setBenefits((prev) => prev.filter((b) => b.id !== id));

            Swal.fire({
              icon: 'success',
              toast: true,
              position: 'top-end',
              timer: 1500,
              showConfirmButton: false,
              title: 'Deleted Successfully'
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Failed!',
              text: response.data.message || 'Unable to delete the item.',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000
            });
          }
        } catch (error) {
          console.error('API Delete Error:', error);
          Swal.fire({
            icon: 'error',
            title: 'Oops!',
            text: error.response?.data?.message || 'Something went wrong while deleting.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });
        }
      }
    });
  };

  const handleNewBenefit = (event) => {
    const url = `/tierCreation`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="enrollment-container">

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button
          className="create-button-new"
          onClick={(e) => handleNewBenefit(e)}
        >
          <span className="create-button__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="23"
              height="23"
              className="create-button__svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="white"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="create-button__label">
            Create tier
          </span>
        </button>

      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={refreshData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {!loading && !error && filtered.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="tt-table-container">
        {loading ? (
          <div className="tt-loading">
            <div className="tt-loading-spinner"></div>
            <p>Loading TierList...</p>
          </div>
        ) : (
          <>
            <table className="tt-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')}>
                    <div className="th-content">
                      <span>Tier Name</span>
                      <FiChevronDown
                        className={`sort-icon ${sortConfig.key === 'name' ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="th-content">
                      <span>Academies</span>
                    </div>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {currentRows.length > 0 ? currentRows.map((b) => (
                  <tr key={b.id}>
                    <td>{b.name}</td>
                    <td >
                      <ul style={{ paddingLeft: '10px' }}>
                        {b.academyNames.map((name, idx) => (
                          <li key={idx}>{name}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="action-buttons" style={{ borderBottom: 'none' }}>
                      <button className="edit-btn tooltip-btn" onClick={(e) => handleEdit(e, b.id)} title="Edit">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                        </svg>
                        <span className="tooltip-text">Edit</span>
                      </button>
                      <button className="delete-btn tooltip-btn" onClick={() => handleDelete(b.id)} title="Delete">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                        </svg>
                        <span className="tooltip-text">Delete</span>
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: "center" }}>No Tier List found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default TierList
